/**
 * Configuration for the self-service Kusto access feature (Kusto page + Permissions page).
 *
 * Endpoint / URL values are sourced from the build-time environment (`.env`, injected as
 * `process.env.REACT_APP_*`) so that no endpoint literal is hard-coded in the component source —
 * mirroring the existing `REACT_APP_KUSTO_URL` pattern and keeping the Breeze "Hard-Coded
 * Endpoints" scanner satisfied. These are also shared here so the two pages stay in sync.
 */

import {
    getConfig,
    GlobalConfigProperties,
} from '../../../Common/api/ABHub/ABHubGlobalConfig';

/** Host of the Kusto cluster backing the SovereignReleaseStatus database (display only). */
export const KUSTO_CLUSTER = process.env.REACT_APP_KUSTO_CLUSTER ?? '';

/**
 * Host of the cluster for the cloud the Hub is running in. Cloud-specific values follow the
 * `REACT_APP_KUSTO_<CLOUD>_CLUSTER` convention already used for per-cloud SLAs. A cloud with no
 * value configured deliberately resolves to empty rather than falling back to the public cluster,
 * since showing the public endpoint inside a sovereign cloud would be wrong.
 */
export const getKustoCluster = (cloudName?: string): string => {
    if (!cloudName || cloudName.toUpperCase() === 'PUBLIC') {
        return KUSTO_CLUSTER;
    }

    return (
        process.env[`REACT_APP_KUSTO_${cloudName.toUpperCase()}_CLUSTER`] ?? ''
    );
};

/** Host of the cluster for the cloud this Hub instance is serving. */
export const getKustoClusterForCurrentCloud = (): string =>
    getKustoCluster(getConfig(GlobalConfigProperties.Cloud));

/**
 * Whether Kusto access has been rolled out to this cloud. Gates the pages so a cloud that has no
 * cluster configured shows nothing at all, rather than a page whose every call fails.
 */
export const isKustoAccessConfiguredForCloud = (): boolean =>
    getKustoClusterForCurrentCloud() !== '';

/** Name of the Kusto database access is managed against (display only). */
export const KUSTO_DATABASE = process.env.REACT_APP_KUSTO_DATABASE ?? '';

/** Core Identity entitlement URL used for the user-access (non-application) flow. */
export const CORE_IDENTITY_URL = process.env.REACT_APP_CORE_IDENTITY_URL ?? '';
